import { useState } from "react";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import type { Content, Lesson } from "api/services/content/schemas/lessons";
import { PreviewContainer } from "routes/library/components/PreviewContainer";
import { StyledTags } from "routes/library/components/styles";
import { Counter } from "routes/patients/PatientProfile/components/UserCourse/components/Counter";
import { JointTag } from "routes/patients/PatientProfile/components/UserCourse/components/SharedStyledComponents";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { parseHTML } from "utils/parseHTML";
import { translatePainLocation } from "utils/translations";

interface Props {
  boxShadow?: boolean;
  defaultPart?: number;
  isEditable?: boolean;
  lesson: Pick<Lesson, "name" | "parts" | "target_joints">;
}

export const LessonPreview: React.FC<Props> = ({ boxShadow, defaultPart = 1, isEditable, lesson }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPart, setCurrentPart] = useState(defaultPart);
  const selectedPart = lesson?.parts?.[currentPart - 1];

  const { target_joints } = lesson;
  const { order, translation } = selectedPart || {};
  const { content, questions, title } = translation || {};

  const [currentQuestion, setCurrentQuestion] = useState(1);
  const selectedQuestion = questions?.[currentQuestion - 1];

  const translatedTags = target_joints?.map(tag => translatePainLocation({ painLocation: tag }));
  const lessonContent = typeof content === "string" ? (JSON.parse(content) as Content[]) : (content as Content[]);
  const hasMoreThanOnePart = lesson?.parts.length > 1;

  const handlePartChange = (newPart: number) => {
    setCurrentPart(newPart);
    setCurrentQuestion(1);
  };

  return (
    <PreviewContainer
      title={`${title} ${hasMoreThanOnePart ? order : ""}`}
      topContent={
        <>
          {hasMoreThanOnePart && (
            <Counter
              value={currentPart}
              maxValue={lesson?.parts.length ?? 1}
              minValue={1}
              clickCallback={handlePartChange}
            />
          )}

          {isEditable && (
            <IconButton onClick={() => navigate("edit")} size="large" style={{ marginLeft: "auto" }}>
              <EditIcon />
            </IconButton>
          )}
        </>
      }
      showBoxShadow={boxShadow}
    >
      <Box display="flex" justifyContent="center" alignItems="flex-start" flexWrap="wrap" gap={5}>
        <Box maxWidth={650} minWidth={300} flex={2}>
          {translatedTags.length > 0 && (
            <TagsBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.target_joints")}
              </Typography>
              <StyledTags>
                {translatedTags.map(tag => (
                  <JointTag key={tag} $variant="blue">
                    {tag}
                  </JointTag>
                ))}
              </StyledTags>
            </TagsBox>
          )}

          {lessonContent &&
            lessonContent.map(({ type, content: c }, index: number) => {
              const key = `${lesson.name}-${order}-${index}`;
              switch (type) {
                case "heading":
                  return (
                    <Typography variant="h4" key={key} mb={1}>
                      {c}
                    </Typography>
                  );
                case "paragraph":
                  return (
                    <Typography key={key} variant="body_thin" dangerouslySetInnerHTML={{ __html: parseHTML(c) }} />
                  );
                case "image":
                  return <Image key={key} src={c} alt={title} />;
                default:
                  return null;
              }
            })}
        </Box>

        {questions && questions.length > 0 && (
          <QuestionContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="body_bold">{t("library.lessons.fields.questions")}</Typography>
              {questions.length > 1 && (
                <ButtonsGroup
                  group={questions.map((question, index) => ({
                    value: (index + 1).toString(),
                    label: `${t("library.lessons.fields.question")} ${(index + 1).toString()}`,
                  }))}
                  active={currentQuestion.toString()}
                  setActive={value => setCurrentQuestion(Number(value))}
                />
              )}
            </Box>

            {selectedQuestion && (
              <>
                <Typography variant="body_bold" mb={1}>
                  {currentQuestion}. {selectedQuestion.title}
                </Typography>
                {selectedQuestion.answers && selectedQuestion.answers.length > 0 && (
                  <Stack spacing={0.5}>
                    {selectedQuestion.answers.map((answer, answerIndex) => (
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${lesson.name}-question-${currentQuestion}-answer-${answerIndex}`}
                      >
                        <FontAwesomeIcon
                          icon={answer.correct ? faCircleCheck : faCircleXmark}
                          color={answer.correct ? "#4BB543" : "#FF0000"}
                        />
                        <Typography variant="body_small">{answer.text}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
                {selectedQuestion.tip_description && (
                  <Box mt={2}>
                    <Typography variant="body_bold" mb={1}>
                      {t("library.lessons.fields.tip_description")}
                    </Typography>
                    <Typography variant="body_small">{selectedQuestion.tip_description}</Typography>
                  </Box>
                )}
              </>
            )}
          </QuestionContainer>
        )}
      </Box>
    </PreviewContainer>
  );
};

const Image = styled.img`
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const QuestionContainer = styled.div`
  border-radius: 8px;
  min-width: 300px;
  flex: 1;
  padding: 24px;
  background-color: ${props => props.theme.colors.redesign.db1};
`;

const TagsBox = styled.div`
  padding-bottom: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${props => props.theme.colors.redesign.db20};
`;
